import type { Moment } from "moment"
import moment from "moment"
import { alpha, darken, lighten, styled, useTheme } from "@mui/material/styles"
import { PickersDay, type PickersDayProps } from "@mui/x-date-pickers/PickersDay"
import clsx from "clsx"
import type { MouseEvent } from "react"
import { toCalendarDay } from "./date-calendar-utils"
import { useRangeSelectionContext } from "./RangeSelectionContext"

type RangePickersDayProps = PickersDayProps<Moment>

const DayButton = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isStart" &&
    prop !== "isEnd" &&
    prop !== "inRange" &&
    prop !== "isToday" &&
    prop !== "spanMultipleDays"
})<{
  isStart: boolean
  isEnd: boolean
  inRange: boolean
  isToday: boolean
  spanMultipleDays: boolean
}>(({ theme, isStart, isEnd, inRange, isToday, spanMultipleDays }) => {
  const isDark = theme.palette.mode === "dark"
  /** ~15% stronger than prior #F3F4F6 / 8% white strip */
  const rangeBg = isDark ? alpha(theme.palette.common.white, 0.11) : "#DADEE5"
  const rangeText = theme.palette.text.primary
  const primaryMain = theme.palette.primary.main
  const primaryContrast = theme.palette.primary.contrastText
  const primaryHoverBg = isDark ? lighten(primaryMain, 0.12) : darken(primaryMain, 0.06)

  return {
    borderRadius: "8px",
    margin: "0 1px",
    ...(inRange &&
      !isStart &&
      !isEnd && {
        backgroundColor: rangeBg,
        color: rangeText,
        borderRadius: 0
      }),
    ...(isStart &&
      !isEnd && {
        backgroundColor: primaryMain,
        color: primaryContrast,
        borderTopLeftRadius: "8px",
        borderBottomLeftRadius: "8px",
        borderTopRightRadius: spanMultipleDays ? 0 : "8px",
        borderBottomRightRadius: spanMultipleDays ? 0 : "8px"
      }),
    ...(isEnd &&
      !isStart && {
        backgroundColor: primaryMain,
        color: primaryContrast,
        borderTopRightRadius: "8px",
        borderBottomRightRadius: "8px",
        borderTopLeftRadius: spanMultipleDays ? 0 : "8px",
        borderBottomLeftRadius: spanMultipleDays ? 0 : "8px"
      }),
    ...(isStart &&
      isEnd && {
        backgroundColor: primaryMain,
        color: primaryContrast,
        borderRadius: "8px"
      }),
    ...(isToday &&
      !isStart &&
      !isEnd &&
      !inRange && {
        fontWeight: 700
      }),
    "&:hover": {
      ...(inRange || isStart || isEnd
        ? {
            backgroundColor: isStart || isEnd ? primaryHoverBg : alpha(rangeBg, isDark ? 1 : 0.9),
            color: isStart || isEnd ? primaryContrast : rangeText
          }
        : {})
    }
  }
})

function sameDay(a: Moment | null, b: Moment | null) {
  if (!a?.isValid() || !b?.isValid()) return false
  return toCalendarDay(a).isSame(toCalendarDay(b), "day")
}

export function RangePickersDay(props: RangePickersDayProps) {
  const { day, outsideCurrentMonth, disabled, ...other } = props
  const theme = useTheme()

  if (disabled) {
    return <PickersDay {...props} />
  }

  const { range, hover, setHover } = useRangeSelectionContext()
  const [start, end] = range

  const d = toCalendarDay(moment(day))
  const s = start?.isValid() ? toCalendarDay(start) : null
  const e = end?.isValid() ? toCalendarDay(end) : null

  const hoverEnd = hover?.isValid() && s && !e ? toCalendarDay(hover) : e
  const rangeEnd = hoverEnd ?? e

  let isStart = !!(s && sameDay(d, s))
  let isEnd = !!(rangeEnd && sameDay(d, rangeEnd))
  let inRange = false

  if (s && rangeEnd?.isValid() && !sameDay(s, rangeEnd)) {
    const lo = s.isBefore(rangeEnd, "day") ? s : rangeEnd
    const hi = s.isBefore(rangeEnd, "day") ? rangeEnd : s
    inRange = d.isAfter(lo, "day") && d.isBefore(hi, "day")
    isStart = sameDay(d, lo)
    isEnd = sameDay(d, hi)
  } else if (s && rangeEnd?.isValid() && sameDay(s, rangeEnd)) {
    isStart = sameDay(d, s)
    isEnd = sameDay(d, s)
  }

  const isToday = sameDay(d, toCalendarDay(moment()))
  const spanMultipleDays = !!(s && rangeEnd?.isValid() && !sameDay(s, rangeEnd))

  return (
    <DayButton
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      disableMargin
      onMouseEnter={(ev: MouseEvent<HTMLButtonElement>) => {
        setHover(toCalendarDay(moment(day)))
        other.onMouseEnter?.(ev, day)
      }}
      className={clsx(isToday && !outsideCurrentMonth && "range-day--today")}
      isStart={isStart}
      isEnd={isEnd}
      inRange={inRange}
      isToday={!!isToday && !outsideCurrentMonth}
      spanMultipleDays={spanMultipleDays}
      sx={{
        ...props.sx,
        ...(outsideCurrentMonth
          ? {
              color: theme.palette.text.secondary,
              opacity: 0.92
            }
          : {})
      }}
    />
  )
}
